// Sign-in: who is signed in, starting Google sign-in and signing out. Better Auth serves /api/auth/*.
// The router uses these to keep signed-out visitors on /signin, signed-in ones off it, and anyone who hasn't
// done onboarding on /welcome.
import type { Me, MeLimits } from '@shared/api';
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { type LoaderFunctionArgs, redirect } from 'react-router';
import { ApiError, api } from '@/lib/api';
import { queryClient } from '@/lib/query';

const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: () => api<Me>('/me'),
  retry: false,
  staleTime: Infinity,
  meta: { signInCheck: true },
});

export function useMe(): Me {
  return useSuspenseQuery(meQuery).data;
}

/** What the learner's plan opens right now: asked again whenever a page that needs it opens (docs/plans/limits.md). */
export function useLimits(): MeLimits {
  return useSuspenseQuery({ ...meQuery, staleTime: 0 }).data.limits;
}

/** Route loader for the app: anyone not signed in, or no longer invited, goes to /signin; onboarding comes first. */
export async function signedInOnly({ request }: LoaderFunctionArgs) {
  let me: Me;
  try {
    me = await queryClient.ensureQueryData(meQuery);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) throw redirect('/signin');
    if (error instanceof ApiError && error.code === 'blocked') throw redirect('/signin?error=blocked');
    if (error instanceof ApiError && error.status === 403) throw redirect('/signin?error=not_invited');
    throw error;
  }
  const url = new URL(request.url);
  const onWelcome = url.pathname === '/welcome';
  // Onboarding first, then Today; a cheat sheet opened from the public site opens instead.
  if (!me.onboarded && !onWelcome)
    throw redirect(
      url.pathname.startsWith('/practice/sheets/')
        ? `/welcome?next=${encodeURIComponent(url.pathname + url.search)}`
        : '/welcome',
    );
  if (me.onboarded && onWelcome) throw redirect('/');
  return null;
}

/** After onboarding is saved, the app opens without asking the server again. */
export function markOnboarded(): void {
  queryClient.setQueryData(meQuery.queryKey, (me) => me && { ...me, onboarded: true });
}

/** Route loader for /signin: someone already signed in goes straight to Today. */
export async function signedOutOnly() {
  try {
    await queryClient.ensureQueryData(meQuery);
  } catch {
    return null;
  }
  throw redirect('/');
}

/** Off to Google. It comes back to `next` (Today unless a page asks for another), or to /signin?error=… if something
 * went wrong. */
export async function startGoogleSignIn(next = '/'): Promise<void> {
  const { url } = await api<{ url: string }>('/auth/sign-in/social', {
    method: 'POST',
    body: JSON.stringify({ provider: 'google', callbackURL: next, errorCallbackURL: '/signin' }),
  });
  window.location.assign(url);
}

export async function signOut(): Promise<void> {
  await api('/auth/sign-out', { method: 'POST', body: '{}' });
  // A full page load, so nothing from this session stays in memory.
  window.location.assign('/signin');
}

/** Delete my data: the account and everything in it, for good. Then sign-in, as after signing out. */
export async function deleteAccount(): Promise<void> {
  await api('/me', { method: 'DELETE' });
  window.location.assign('/signin');
}
